import * as RS from '@radix-ui/react-slider';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from './cn';

/**
 * Ant Design `Slider` replacement (single value or `range`), built on
 * @radix-ui/react-slider. onChange emits `number` (single) or `[number, number]`
 * (range), matching antd.
 */
export interface SliderProps {
  min?: number;
  max?: number;
  step?: number;
  value?: number | [number, number];
  defaultValue?: number | [number, number];
  onChange?: (value: any) => void;
  onChangeComplete?: (value: any) => void;
  range?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  /** antd tooltip config — accepted; a value bubble shows on the thumb when a formatter is given. */
  tooltip?: boolean | { formatter?: (v: number) => ReactNode; open?: boolean };
  marks?: Record<number, ReactNode>;
}

const toArr = (v?: number | [number, number]): number[] | undefined =>
  v == null ? undefined : Array.isArray(v) ? v : [v];

export function Slider({
  min = 0, max = 100, step = 1, value, defaultValue, onChange, onChangeComplete, range, disabled, className, style, tooltip,
}: SliderProps) {
  const fromArr = (a: number[]) => (range ? ([a[0], a[a.length - 1]] as [number, number]) : a[0]);
  const fmt = typeof tooltip === 'object' && tooltip.formatter ? tooltip.formatter : undefined;
  const cur = toArr(value) ?? toArr(defaultValue) ?? (range ? [min, max] : [min]);
  return (
    <RS.Root
      className={cn('relative flex h-5 w-full touch-none select-none items-center', className)}
      style={style}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      value={toArr(value)}
      defaultValue={toArr(defaultValue) ?? (range ? [min, max] : [min])}
      onValueChange={(a) => onChange?.(fromArr(a))}
      onValueCommit={(a) => onChangeComplete?.(fromArr(a))}
    >
      <RS.Track className="relative h-1 w-full grow rounded-full bg-surface-3">
        <RS.Range className="absolute h-full rounded-full bg-brand-500" />
      </RS.Track>
      {(range ? [0, 1] : [0]).map((i) => (
        <RS.Thumb
          key={i}
          className="group relative block size-4 rounded-full border-2 border-brand-500 bg-surface shadow-soft transition-colors hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-50"
          aria-label={range ? (i === 0 ? 'Minimum' : 'Maximum') : 'Value'}
        >
          {fmt && (
            <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-[#1f2a22] px-1.5 py-0.5 type-caption text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
              {fmt(cur[i] ?? min)}
            </span>
          )}
        </RS.Thumb>
      ))}
    </RS.Root>
  );
}
